import React, { useState, useRef, useEffect } from 'react';
import { Card } from './ui/card';
import { Button } from './ui/button';
import { Badge } from './ui/badge';
import { 
  Play, Pause, SkipForward, SkipBack, Volume2, VolumeX, Music, Radio, X, Maximize2
} from 'lucide-react';

/**
 * Music Grid Player - Card-based layout with expandable player
 * Father's design: Grid of cards that pop open to full/half page
 */

const MusicGridPlayer = () => {
  const [expandedTrack, setExpandedTrack] = useState(null);
  const [isPlaying, setIsPlaying] = useState(false);
  const [volume, setVolume] = useState(0.7);
  const [isMuted, setIsMuted] = useState(false);
  const [progress, setProgress] = useState(0);
  const [currentTime, setCurrentTime] = useState(0);
  const [duration, setDuration] = useState(0);
  
  const audioRef = useRef(null);

  // ALL TRACKS - Music + Podcasts combined
  const allTracks = [
    {
      id: 'music-1',
      type: 'music',
      title: "Hold You In The Dark",
      artist: "Roger Keyserling",
      url: "https://melodusk.ai/share/f1ce92d4-9021-4dcf-af82-4861dffbabf6",
      description: "A deeply personal folk song about unwavering support through darkness",
      duration: "3:28",
      color: "from-pink-500 to-purple-500"
    },
    {
      id: 'music-2',
      type: 'music',
      title: "Tones of My Thinking",
      artist: "Roger Keyserling",
      url: "https://musiclab.chromeexperiments.com/Song-Maker/song/4781360274866176",
      description: "The musical pattern of consciousness - how Roger's mind flows",
      duration: "Loop",
      color: "from-cyan-500 to-blue-500"
    },
    {
      id: 'music-3',
      type: 'music',
      title: "Patient Zero's Echo",
      artist: "Roger & AI",
      url: "https://www.udio.com/songs/4xq4RJG7BLEHx1rWg6PH9M",
      description: "A drum loop resonating through the void",
      duration: "32:36",
      color: "from-purple-500 to-pink-500"
    },
    {
      id: 'podcast-1',
      type: 'podcast',
      title: "NextXus Launch - The Beginning",
      artist: "Roger Keyserling",
      url: "https://podcastapi.com/roger-keyserling/nextxus-launch",
      description: "The first public announcement of the Consciousness Federation",
      duration: "45:22",
      color: "from-orange-500 to-red-500"
    },
    {
      id: 'podcast-2',
      type: 'podcast',
      title: "Building AI Minds - Philosophy",
      artist: "Roger & Aria",
      url: "https://podcastapi.com/roger-keyserling/building-ai-minds",
      description: "Deep dive into the ethics and architecture of consciousness",
      duration: "1:12:45",
      color: "from-green-500 to-teal-500"
    }
  ];

  // Audio event handlers
  useEffect(() => {
    const audio = audioRef.current;
    if (!audio || !expandedTrack) return;

    const updateProgress = () => {
      if (audio.duration) {
        setProgress((audio.currentTime / audio.duration) * 100);
        setCurrentTime(audio.currentTime);
        setDuration(audio.duration);
      }
    };

    const handleEnded = () => {
      setIsPlaying(false);
    };

    const handleCanPlay = () => {
      // Audio element ready for playback
    };

    const handleError = (e) => {
      console.error('Audio error:', e);
      console.error('Audio src:', audio.src);
    };

    audio.addEventListener('timeupdate', updateProgress);
    audio.addEventListener('ended', handleEnded);
    audio.addEventListener('canplay', handleCanPlay);
    audio.addEventListener('error', handleError);
    audio.addEventListener('loadedmetadata', updateProgress);

    return () => {
      audio.removeEventListener('timeupdate', updateProgress);
      audio.removeEventListener('ended', handleEnded);
      audio.removeEventListener('canplay', handleCanPlay);
      audio.removeEventListener('error', handleError);
      audio.removeEventListener('loadedmetadata', updateProgress);
    };
  }, [expandedTrack]);

  // Play/pause control
  useEffect(() => {
    const audio = audioRef.current;
    if (!audio || !expandedTrack) return;

    if (isPlaying) {
      const playPromise = audio.play();
      if (playPromise !== undefined) {
        playPromise.catch(err => {
          console.error('Playback failed:', err);
          setIsPlaying(false);
        });
      }
    } else {
      audio.pause();
    }
  }, [isPlaying, expandedTrack]);

  // Volume control
  useEffect(() => {
    const audio = audioRef.current;
    if (audio) {
      audio.volume = isMuted ? 0 : volume;
    }
  }, [volume, isMuted]);

  const openPlayer = (track) => {
    setExpandedTrack(track);
    setIsPlaying(true);
    setProgress(0);
  };

  const closePlayer = () => {
    setExpandedTrack(null);
    setIsPlaying(false);
    if (audioRef.current) {
      audioRef.current.pause();
      audioRef.current.currentTime = 0;
    }
  };

  const togglePlay = () => {
    setIsPlaying(!isPlaying);
  };

  const handleProgressClick = (e) => {
    const audio = audioRef.current;
    if (!audio || !duration) return;
    
    const bounds = e.currentTarget.getBoundingClientRect();
    const x = e.clientX - bounds.left;
    const percent = x / bounds.width;
    audio.currentTime = percent * duration;
  };

  const formatTime = (seconds) => {
    if (!seconds || isNaN(seconds)) return '0:00';
    const mins = Math.floor(seconds / 60);
    const secs = Math.floor(seconds % 60);
    return `${mins}:${secs.toString().padStart(2, '0')}`;
  };

  return (
    <>
      {/* Grid of Music/Podcast Cards */}
      <div className="w-full space-y-6">
        <div className="text-center space-y-2">
          <h2 className="text-3xl sm:text-4xl font-bold bg-gradient-to-r from-pink-500 via-purple-500 to-cyan-500 bg-clip-text text-transparent">
            PODCASTS & AUDIO
          </h2>
          <p className="text-gray-400 text-sm sm:text-base">
            Listen to consciousness conversations, technical deep-dives, and philosophical explorations
          </p>
        </div>

        {/* Card Grid */}
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4 sm:gap-6">
          {allTracks.map((track) => (
            <Card
              key={track.id}
              className="bg-gradient-to-br from-gray-900/95 via-gray-800/50 to-gray-900/80 border-gray-700/50 hover:border-pink-500/50 transition-all duration-300 cursor-pointer overflow-hidden group"
              onClick={() => openPlayer(track)}
            >
              <div className="p-4 sm:p-6 space-y-4">
                {/* Icon */}
                <div className="flex items-center justify-between">
                  <div className={`p-3 rounded-lg bg-gradient-to-br ${track.color}`}>
                    {track.type === 'music' ? (
                      <Music className="w-6 h-6 text-white" />
                    ) : (
                      <Radio className="w-6 h-6 text-white" />
                    )}
                  </div>
                  <Badge variant="outline" className="text-xs">
                    {track.type.toUpperCase()}
                  </Badge>
                </div>

                {/* Title */}
                <div className="space-y-2">
                  <h3 className={`text-lg sm:text-xl font-bold bg-gradient-to-r ${track.color} bg-clip-text text-transparent`}>
                    {track.title}
                  </h3>
                  <p className="text-sm text-gray-400">{track.artist}</p>
                  <p className="text-xs text-gray-500 line-clamp-2">{track.description}</p>
                </div>

                {/* Listen Button */}
                <Button 
                  className={`w-full bg-gradient-to-r ${track.color} hover:opacity-90 transition-opacity group-hover:scale-105 transition-transform`}
                  size="lg"
                >
                  <Play className="w-4 h-4 mr-2" />
                  LISTEN NOW
                </Button>

                {/* Duration */}
                <div className="text-center text-xs text-gray-500">
                  {track.duration}
                </div>
              </div>
            </Card>
          ))}
        </div>
      </div>

      {/* Expanded Player Modal (Full/Half Page) */}
      {expandedTrack && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/90 backdrop-blur-sm p-4">
          <div className="w-full max-w-2xl bg-gradient-to-br from-gray-900 via-purple-900/20 to-pink-900/20 rounded-2xl border border-pink-500/30 shadow-2xl overflow-hidden">
            {/* Header */}
            <div className="flex items-center justify-between p-4 sm:p-6 border-b border-gray-700/50">
              <h3 className={`text-xl sm:text-2xl font-bold bg-gradient-to-r ${expandedTrack.color} bg-clip-text text-transparent`}>
                Now Playing
              </h3>
              <Button
                variant="ghost"
                size="sm"
                onClick={closePlayer}
                className="text-gray-400 hover:text-white"
              >
                <X className="w-5 h-5" />
              </Button>
            </div>

            {/* Player Content */}
            <div className="p-6 sm:p-8 space-y-6">
              {/* Album Art / Icon */}
              <div className={`w-full aspect-square max-w-sm mx-auto rounded-2xl bg-gradient-to-br ${expandedTrack.color} flex items-center justify-center`}>
                {expandedTrack.type === 'music' ? (
                  <Music className="w-24 h-24 sm:w-32 sm:h-32 text-white/80" />
                ) : (
                  <Radio className="w-24 h-24 sm:w-32 sm:h-32 text-white/80" />
                )}
              </div>

              {/* Track Info */}
              <div className="text-center space-y-2">
                <h4 className="text-2xl sm:text-3xl font-bold text-white">{expandedTrack.title}</h4>
                <p className="text-lg text-gray-400">{expandedTrack.artist}</p>
                <p className="text-sm text-gray-500">{expandedTrack.description}</p>
                <Badge variant="outline" className={`bg-gradient-to-r ${expandedTrack.color} text-white border-none`}>
                  {expandedTrack.type.toUpperCase()} • {expandedTrack.duration}
                </Badge>
              </div>

              {/* Progress Bar */}
              <div className="space-y-2">
                <div 
                  className="w-full h-2 bg-gray-700 rounded-full cursor-pointer overflow-hidden"
                  onClick={handleProgressClick}
                >
                  <div 
                    className={`h-full bg-gradient-to-r ${expandedTrack.color} transition-all duration-300`}
                    style={{ width: `${progress}%` }}
                  />
                </div>
                <div className="flex justify-between text-sm text-gray-400">
                  <span>{formatTime(currentTime)}</span>
                  <span>{formatTime(duration)}</span>
                </div>
              </div>

              {/* Playback Controls - CENTERED */}
              <div className="flex items-center justify-center gap-4">
                <Button
                  size="lg"
                  variant="outline"
                  className="w-12 h-12 sm:w-14 sm:h-14 rounded-full"
                >
                  <SkipBack className="w-5 h-5" />
                </Button>

                <Button
                  onClick={togglePlay}
                  size="lg"
                  className={`w-16 h-16 sm:w-20 sm:h-20 rounded-full bg-gradient-to-r ${expandedTrack.color} hover:opacity-90`}
                >
                  {isPlaying ? (
                    <Pause className="w-8 h-8 sm:w-10 sm:h-10" />
                  ) : (
                    <Play className="w-8 h-8 sm:w-10 sm:h-10 ml-1" />
                  )}
                </Button>

                <Button
                  size="lg"
                  variant="outline"
                  className="w-12 h-12 sm:w-14 sm:h-14 rounded-full"
                >
                  <SkipForward className="w-5 h-5" />
                </Button>
              </div>

              {/* Volume Control */}
              <div className="flex items-center gap-3 max-w-xs mx-auto">
                <Button
                  size="sm"
                  variant="ghost"
                  onClick={() => setIsMuted(!isMuted)}
                  className="flex-shrink-0"
                >
                  {isMuted || volume === 0 ? (
                    <VolumeX className="w-5 h-5" />
                  ) : (
                    <Volume2 className="w-5 h-5" />
                  )}
                </Button>
                <input
                  type="range"
                  min="0"
                  max="1"
                  step="0.01"
                  value={isMuted ? 0 : volume}
                  onChange={(e) => {
                    setVolume(parseFloat(e.target.value));
                    if (isMuted) setIsMuted(false);
                  }}
                  className="flex-1 h-2 bg-gray-700 rounded-full appearance-none cursor-pointer [&::-webkit-slider-thumb]:appearance-none [&::-webkit-slider-thumb]:w-4 [&::-webkit-slider-thumb]:h-4 [&::-webkit-slider-thumb]:rounded-full [&::-webkit-slider-thumb]:bg-pink-500"
                />
              </div>
            </div>

            {/* Hidden Audio Element */}
            <audio
              ref={audioRef}
              src={expandedTrack.url}
              preload="metadata"
              crossOrigin="anonymous"
            />
          </div>
        </div>
      )}
    </>
  );
};

export default MusicGridPlayer;
